import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import BusinessSwitcher from './BusinessSwitcher';
import UpgradeModal from './UpgradeModal';
import {
  UserRole, PlanFeature,
  getLockedFeature, isPlanSufficient,
  getSidebarLinks, getPlanFeatures, getAudience,
} from '../config/permissions';
import API_URL from '../config/api';

/* ── Landlord live-badge state ────────────────────────────────── */
interface LandlordBadges {
  unpaidRent: number;    // red  — Rent Tracker
  openMaint: number;     // amber — Maintenance
  expiringCerts: number; // amber — Docs & Certs
}

const Sidebar = () => {
  const { token, role, business_id, plan, business_type, userName } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState<PlanFeature | null>(null);
  const [landlordBadges, setLandlordBadges] = useState<LandlordBadges>({ unpaidRent: 0, openMaint: 0, expiringCerts: 0 });

  const isLandlord = getAudience(business_type) === 'landlord';

  /* ── Landlord live badges — fetched on mount + every 5 min ── */
  const fetchLandlordBadges = useCallback(async () => {
    if (!token || !isLandlord) return;
    try {
      const month = new Date().toISOString().slice(0, 7);
      const res = await fetch(`${API_URL}/landlord/dashboard?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const d = await res.json();
      const props: any[] = d.properties || [];
      const isOccupied = (p: any) => !!p.tenant_name || !!p.tenancy_id;
      const isPartial  = (p: any) => !p.rent_paid && p.amount_paid != null && Number(p.amount_paid) > 0;
      const unpaidRent    = props.filter(p => isOccupied(p) && !p.rent_paid && !isPartial(p)).length;
      const openMaint     = props.reduce((s: number, p: any) => s + (Number(p.open_maintenance) || 0), 0);
      const expiringCerts = props.reduce((s: number, p: any) => s + (Number(p.expiring_certs)   || 0), 0);
      setLandlordBadges({ unpaidRent, openMaint, expiringCerts });
    } catch { /* silent */ }
  }, [token, isLandlord]);

  useEffect(() => {
    fetchLandlordBadges();
    if (!isLandlord) return;
    const id = setInterval(fetchLandlordBadges, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchLandlordBadges, isLandlord]);

  if (!token) return null;
  if (role === 'super_admin') return null;

  const audience = getAudience(business_type);
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Pick the correct sidebar links based on business type and current role
  const currentRole = role as UserRole | undefined;
  const visibleLinks = getSidebarLinks(business_type).filter(item =>
    currentRole ? item.roles.includes(currentRole) : false
  );
  // Pick the correct plan features for locking
  const planFeatures = getPlanFeatures(business_type);
  const getFeatureLock = (path: string) => planFeatures.find(f => path.startsWith(f.path));

  // Build ordered group list (preserving first-occurrence order)
  const groups: string[] = [];
  visibleLinks.forEach(l => { if (!groups.includes(l.group)) groups.push(l.group); });

  const linkStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    marginBottom: 2,
    backgroundColor: active ? (isLandlord ? '#f0fdfa' : '#1d4ed8') : 'transparent',
    borderRadius: 7,
    textDecoration: 'none',
    color: active ? (isLandlord ? '#0d9488' : '#fff') : '#374151',
    fontWeight: active ? 700 : 500,
    fontSize: 13.5,
    transition: 'background 0.15s, color 0.15s',
    borderLeft: active && isLandlord ? '3px solid #0d9488' : '3px solid transparent',
  });

  const groupLabelStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#9ca3af',
    padding: '12px 12px 4px',
    marginTop: 4,
  };

  return (
    <div style={{
      width: 220,
      minWidth: 220,
      padding: '12px 10px',
      backgroundColor: '#f8fafc',
      height: '100vh',
      overflowY: 'auto',
      borderRight: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
    }}>
      <div style={{ marginBottom: 8 }}>
        <BusinessSwitcher />
      </div>
      <nav style={{ flex: 1 }}>
        {groups.map(group => (
          <div key={group}>
            <div style={groupLabelStyle}>{group}</div>
            {visibleLinks.filter(l => l.group === group).map(item => {
              const lockedFeature = getFeatureLock(item.path);
              const locked = lockedFeature ? !isPlanSufficient(plan, lockedFeature.requiredPlan) : false;
              const active = isActive(item.path);

              if (locked) {
                return (
                  <div
                    key={item.path}
                    onClick={() => setUpgradeModal(lockedFeature!)}
                    title={`Upgrade to ${lockedFeature!.requiredPlanLabel} to access ${lockedFeature!.featureName}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 8, padding: '8px 12px', marginBottom: 2,
                      borderRadius: 7, cursor: 'pointer', color: '#9ca3af',
                      fontWeight: 500, fontSize: 13.5,
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f9fafb'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16, opacity: 0.5 }}>{item.icon}</span>
                      {item.label}
                    </span>
                    <span style={{ fontSize: 11 }}>🔒</span>
                  </div>
                );
              }

              if (item.external) {
                const href = item.path === '/shop' ? `/shop/${business_id}` : item.path;
                return (
                  <a
                    key={item.path}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px', marginBottom: 2, borderRadius: 7,
                      color: '#059669', fontWeight: 600, fontSize: 13.5,
                      textDecoration: 'none',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = '#ecfdf5'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                  >
                    <span style={{ fontSize: 16 }}>{item.icon}</span>
                    {item.label} ↗
                  </a>
                );
              }
              const linkPath = (role === 'Staff' && item.path === '/customers') ? '/customers/add' : item.path;

              /* landlord live badges */
              const badgeCount =
                isLandlord && item.path === '/landlord/rent'         ? landlordBadges.unpaidRent    :
                isLandlord && item.path === '/landlord/maintenance'   ? landlordBadges.openMaint     :
                isLandlord && item.path === '/landlord/certificates'  ? landlordBadges.expiringCerts : 0;
              const badgeBg = item.path === '/landlord/rent' ? '#dc2626' : '#d97706';

              return (
                <Link
                  key={item.path}
                  to={linkPath}
                  style={{ ...linkStyle(active), justifyContent: 'space-between' }}
                  onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.backgroundColor = '#f0fdfa'; }}
                  onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14 }}>{item.icon}</span>
                    {item.label}
                  </span>
                  {badgeCount > 0 && (
                    <span style={{
                      background: active ? 'rgba(255,255,255,0.3)' : badgeBg,
                      color: '#fff', borderRadius: 20, padding: '1px 7px',
                      fontSize: 11, fontWeight: 800, minWidth: 18, textAlign: 'center',
                      flexShrink: 0,
                    }}>{badgeCount}</span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 8, marginTop: 4 }}>
        {/* ── User footer ── */}
        {isLandlord ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 9,
            padding: '10px 10px 6px', marginTop: 4,
          }}>
            {/* Avatar */}
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg,#0d9488,#14b8a6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 800, color: '#fff',
            }}>
              {(userName || 'U').charAt(0).toUpperCase()}
            </div>
            {/* Name + role */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {userName || 'Landlord'}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {role || 'Admin'}
              </div>
            </div>
            {/* Help icon */}
            <button
              title="Help & Support"
              onClick={() => window.open('mailto:support@shahosuite.com', '_blank')}
              style={{
                width: 26, height: 26, border: '1.5px solid #e5e7eb', borderRadius: '50%',
                background: '#f8fafc', cursor: 'pointer', fontSize: 12, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b',
              }}
            >?</button>
            {/* Logout */}
            <button
              title="Sign out"
              onClick={() => setShowLogoutConfirm(true)}
              style={{
                width: 26, height: 26, border: '1.5px solid #fecaca', borderRadius: '50%',
                background: '#fff1f1', cursor: 'pointer', fontSize: 12, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626',
              }}
            >↪</button>
          </div>
        ) : (
          <button
            onClick={() => setShowLogoutConfirm(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '8px 12px', borderRadius: 7, border: 'none', background: 'transparent',
              color: '#dc2626', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', textAlign: 'left',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = '#fef2f2'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
          >
            🔐 Logout
          </button>
        )}
      </div>

      {/* UpgradeModal */}
      {upgradeModal && (
        <UpgradeModal
          featureName={upgradeModal.featureName}
          featureDescription={upgradeModal.featureDescription}
          requiredPlan={upgradeModal.requiredPlanLabel}
          onClose={() => setUpgradeModal(null)}
        />
      )}

      {/* Logout confirmation overlay */}
      {showLogoutConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '28px 32px', width: 320,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)', textAlign: 'center',
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🔐</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 800, color: '#111827' }}>Sign out?</h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: '#6b7280' }}>Are you sure you want to log out?</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowLogoutConfirm(false); navigate('/logout'); }}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
